import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Send, Trash2, X, MessageCircle, ChevronDown } from 'lucide-react';
import { PACOCA_API_URL } from '../../global-components/src/utils/vars';
import { useAuth } from '../../global-components/src';
import { getImagePathBucketPacoca } from '../../global-components/src/utils/utils';
import Spinner from '../../global-components/src/components/spinner/spiner';
import './story-interactions.css';
import { getNativeKeyboardHeight, getNativeNavigationHeight, isNewApp, isNewAppAndroid } from '../../utils/platform';
import { isIOSWebView } from '../../global-components/src/utils/platform';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface CommentUser {
  id: number;
  name: string;
  user_name: string;
  img_account: string;
}

interface StoryComment {
  id: number;
  id_story: number;
  id_user: number;
  text: string;
  created_at: string;
  user: CommentUser;
}

interface StoryReaction {
  id: number;
  id_story: number;
  id_user: number;
  emoji: string;
}

interface Props {
  storyId: number;
  reactions: StoryReaction[];
  onPause: () => void;
  onResume: () => void;
  isHolding: boolean;
  initiallyOpen?: boolean;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const QUICK_EMOJIS = ['❤️', '😂', '😮', '😢', '🔥', '👏'];

// ─── Componente ───────────────────────────────────────────────────────────────

const StoryInteractions: React.FC<Props> = ({
  storyId,
  reactions,
  onPause,
  onResume,
  isHolding,
  initiallyOpen = false,
}) => {
  const { user, token, isSuper } = useAuth();

  // ── estado local de reações (optimistic) ────────────────────────────────
  const [localReactions, setLocalReactions] = useState<StoryReaction[]>(reactions);
  const [myReaction, setMyReaction] = useState<string | null>(
    reactions.find(r => r.id_user === user?.id)?.emoji ?? null,
  );

  // ── busca reações do banco ao trocar de story ─────────────────────────────
  useEffect(() => {
    const fetchReactions = async () => {
      try {
        const { data } = await axios.get(
          `${PACOCA_API_URL}/stories/${storyId}/reactions`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setLocalReactions(data);
        setMyReaction(data.find((r: StoryReaction) => r.id_user === user?.id)?.emoji ?? null);
      } catch {
        // silencia
      }
    };

    fetchReactions();
  }, [storyId]); // ← roda toda vez que mudar de story

  // ── comentários ─────────────────────────────────────────────────────────
  const [comments, setComments]             = useState<StoryComment[]>([]);
  const [commentText, setCommentText]       = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [panelOpen, setPanelOpen]           = useState(initiallyOpen);
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLDivElement>(null);
  const [bottomPadding, setBottomPadding] = useState(20);
  const openedInitialStoryRef = useRef(false);

  useEffect(() => {
    if (panelOpen) onPause();
  }, [panelOpen]);

  // ── sincroniza reactions quando a story muda ─────────────────────────────
  useEffect(() => {
    setComments([]);

    if (initiallyOpen && !openedInitialStoryRef.current) {
      openedInitialStoryRef.current = true;
      setPanelOpen(true);
      return;
    }

    setPanelOpen(false);
  }, [initiallyOpen, storyId]);

  // ── busca comentários ao abrir painel ────────────────────────────────────
  useEffect(() => {
    if (!panelOpen) return;
    fetchComments();
  }, [panelOpen, storyId]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const { data } = await axios.get(
        `${PACOCA_API_URL}/stories/${storyId}/comments`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setComments(data.data ?? data);
    } catch {
      // silencia
    } finally {
      setLoadingComments(false);
    }
  };

  // ── reação (optimistic update) ───────────────────────────────────────────
  const handleReact = async (emoji: string) => {
    if (!user) return;

    const previous = myReaction;
    const wasToggled = previous === emoji;

    // optimistic
    setMyReaction(wasToggled ? null : emoji);
    setLocalReactions(prev => {
      const without = prev.filter(r => r.id_user !== user.id);
      if (wasToggled) return without;
      return [...without, { id: Date.now(), id_story: storyId, id_user: user.id, emoji }];
    });

    try {
      await axios.post(
        `${PACOCA_API_URL}/stories/${storyId}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      // reverte
      setMyReaction(previous);
      setLocalReactions(reactions);
    }
  };

  // ── enviar comentário ────────────────────────────────────────────────────
  const handleSendComment = async () => {
    const text = commentText.trim();
    if (!text || sendingComment) return;

    setSendingComment(true);
    const optimistic: StoryComment = {
      id: Date.now(),
      id_story: storyId,
      id_user: user!.id,
      text,
      created_at: new Date().toISOString(),
      user: {
        id: user!.id,
        name: user!.name,
        user_name: user!.user_name,
        img_account: user!.img_account,
      },
    };

    setComments(prev => [optimistic, ...prev]);
    setCommentText('');

    try {
      const { data } = await axios.post(
        `${PACOCA_API_URL}/stories/${storyId}/comments`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // substitui optimistic pelo real
      setComments(prev => prev.map(c => (c.id === optimistic.id ? data : c)));
    } catch {
      setComments(prev => prev.filter(c => c.id !== optimistic.id));
      setCommentText(text);
    } finally {
      setSendingComment(false);
    }
  };

  // ── deletar comentário ───────────────────────────────────────────────────
  const handleDeleteComment = async (commentId: number) => {
    setComments(prev => prev.filter(c => c.id !== commentId));
    try {
      await axios.delete(
        `${PACOCA_API_URL}/stories/comments/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      fetchComments(); // reverte
    }
  };

  // ── agrupar reações por emoji ─────────────────────────────────────────────
  const reactionGroups = localReactions.reduce<Record<string, number>>((acc, r) => {
    acc[r.emoji] = (acc[r.emoji] ?? 0) + 1;
    return acc;
  }, {});

  const openPanel = () => {
    setPanelOpen(true);
    onPause();
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  const closePanel = () => {
    setPanelOpen(false);
    onResume();
  };

  useEffect(() => {
      const handleKeyboardHeightChange = () => {
          const nativeNavigationHeight = getNativeNavigationHeight() ?? 0;
          let height = 30;
          if (inputRef.current) height = inputRef?.current.clientHeight;

          setBottomPadding(nativeNavigationHeight);
      };

      window.addEventListener("keyboardHeightChange", handleKeyboardHeightChange);

      return () => {
          window.removeEventListener("keyboardHeightChange", handleKeyboardHeightChange);
      };
  }, []);

  
  function ajustaNewApp() {
      if((isNewApp() && isNewAppAndroid()) || isIOSWebView()){
          const nativeNavigationHeight = getNativeNavigationHeight();
          let height = 30;
          if (inputRef.current) height = inputRef?.current.clientHeight;

          if(nativeNavigationHeight && nativeNavigationHeight > 0){
              setBottomPadding(nativeNavigationHeight);
          }

          window.addEventListener("navigationBarHeightChange", ajustaNewApp);
          return () => window.removeEventListener("navigationBarHeightChange", ajustaNewApp);
      }
  }
  useEffect(() => {
      ajustaNewApp()
  }, []);
  
  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Barra inferior fixa ── */}
      <div className={`si-bar ${isHolding ? 'story-ui-hidden' : ''}`} style={{ bottom: bottomPadding + 20 }}>

        {/* Reações rápidas */}
        <div className="si-quick-emojis">
          {QUICK_EMOJIS.map(emoji => (
            <button
              key={emoji}
              className={`si-emoji-btn ${myReaction === emoji ? 'si-emoji-btn--active' : ''}`}
              onClick={() => handleReact(emoji)}
            >
              <span className="si-emoji-glyph">{emoji}</span>
              {reactionGroups[emoji] ? (
                <span className="si-emoji-count">{reactionGroups[emoji]}</span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Botão comentar */}
        <button className="si-comment-trigger" onClick={openPanel}>
          <MessageCircle size={20} />
          {comments.length > 0 && <span className="si-comment-count">{comments.length}</span>}
        </button>
      </div>

      {/* ── Painel de comentários (bottom-sheet leve) ── */}
      {panelOpen && (
        <div className="si-backdrop" onClick={closePanel}>
          <div
            className="si-panel"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle + header */}
            <div className="si-panel-header">
              <div className="si-handle" />
              <span className="si-panel-title">Comentários</span>
              <button className="si-close-btn" onClick={closePanel}>
                <ChevronDown size={20} />
              </button>
            </div>

            {/* Lista */}
            <div className="si-comment-list" ref={listRef}>
              {loadingComments ? (
                <div className="si-loader"><Spinner size="24px" /></div>
              ) : comments.length === 0 ? (
                <p className="si-empty">Seja o primeiro a comentar!</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} className="si-comment">
                    <img
                      src={getImagePathBucketPacoca(c.user.img_account, "/img/img-account.png")}
                      alt={c.user.name}
                      className="si-comment-avatar"
                    />
                    <div className="si-comment-body">
                      <span className="si-comment-name">{c.user.name}</span>
                      <p className="si-comment-text">{c.text}</p>
                    </div>
                    {(c.id_user === user?.id || isSuper()) && (
                      <button
                        className="si-comment-delete"
                        onClick={() => handleDeleteComment(c.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Input */}
            <div className="si-input-row" style={{ paddingBottom: bottomPadding }}>
              <img
                src={getImagePathBucketPacoca(user?.img_account, "/img/img-account.png")}
                alt={user?.name}
                className="si-comment-avatar"
              />
              <input
                ref={inputRef}
                className="si-input"
                placeholder="Comentar..."
                value={commentText}
                maxLength={500}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendComment()}
              />
              <button
                className="si-send-btn"
                onClick={handleSendComment}
                disabled={!commentText.trim() || sendingComment}
              >
                {sendingComment ? <Spinner size="16px" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StoryInteractions;